import React, {
    useEffect,
    useState,
} from "react";

import { firestore } from "../common/firebase";
import { collection, doc, query, orderBy, limit as fsLimit, onSnapshot } from "firebase/firestore";



// *** System API ***
/*
system = uid => this.db.doc(`systems/${uid}`);
systems = () => this.db.collection('systems');
usersystems = userid => this.systems().where( "userId", "==", `${userid}`);

gateway = uid => this.db.doc(`gateways/${uid}`);
gatewayconfig = uid => this.db.doc(`gateways/${uid}/config/network`);
gatewaystate = uid => this.db.doc(`gateways/${uid}/config/state`);

gateways = () => this.db.collection('gateways');
systemgateways = sysid => this.gateways().where("system_id", "==", `${sysid}`);

scene = uid => this.db.doc( `scenes/${uid}` );
scenes = () =>this.db.collection('scenes');
systemscenes = sysid =>this.scenes().where("system_id", "==", `${sysid}`);
*/

const SERVICE_STALE_MS = 60_000;
const FRESHNESS_TICK_MS = 5_000;

function useCollection(name, limit) {
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([])
    
    useEffect(
        () => {
            const colRef = collection(firestore, name);
            const q = query(colRef, fsLimit(limit || 50));
            const unsubscribe = onSnapshot(
              q,
              (snapshot) => {
                const sys = [];
                snapshot.forEach((d) =>
                  sys.push({ ...d.data(), uid: d.id })
                );
                setData(sys.reverse());
                setLoading(false);
              },
              (err) => {
                setError(err);
              }
            );
            return () => unsubscribe();
        },
        [name, limit]
    )

    return {
        error,
        loading,
        collection: data,
    };
}


function useAllSystems(limit) {
    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(true)
    const [systems, setSystems] = useState([])
    
    useEffect(
        () => {
            const systemsRef = collection(firestore, 'systems');
            const q = query(systemsRef, orderBy('createdAt', 'desc'), fsLimit(limit || 50));
            const unsubscribe = onSnapshot(
              q,
              (snapshot) => {
                const sys = [];
                snapshot.forEach((d) =>
                  sys.push({ ...d.data(), uid: d.id })
                );
                setSystems(sys.reverse());
                setLoading(false);
              },
              (err) => {
                setError(err);
              }
            );
            return () => unsubscribe();
        },
        [limit]
    )

    return {
        error,
        loading,
        systems,
    };
}

function heartbeatAgeMs(updatedAt, nowMs) {
    if (!updatedAt) return null;
    const ms = typeof updatedAt.toMillis === 'function'
        ? updatedAt.toMillis()
        : (updatedAt.seconds != null ? updatedAt.seconds * 1000 : Number(updatedAt));
    if (!Number.isFinite(ms)) return null;
    return Math.max(0, nowMs - ms);
}

function formatLastSeen(ageMs) {
    if (ageMs == null) return 'never';
    if (ageMs < 5_000) return 'just now';
    if (ageMs < 60_000) return `${Math.floor(ageMs / 1000)}s ago`;
    if (ageMs < 3_600_000) return `${Math.floor(ageMs / 60_000)}m ago`;
    return `${Math.floor(ageMs / 3_600_000)}h ago`;
}

/** Live status for scheduler + AI Help via serviceStatus/* heartbeats. */
function useServiceStatus() {
    const [heartbeats, setHeartbeats] = useState({ scheduler: null, 'ai-help': null });
    const [loading, setLoading] = useState(true);
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const ids = ['scheduler', 'ai-help'];
        const seen = new Set();
        const markSeen = (id) => {
            seen.add(id);
            if (seen.size >= ids.length) setLoading(false);
        };
        const unsubs = ids.map((id) =>
            onSnapshot(
                doc(firestore, 'serviceStatus', id),
                (snap) => {
                    setHeartbeats((prev) => ({
                        ...prev,
                        [id]: snap.exists() ? snap.data() : null,
                    }));
                    markSeen(id);
                },
                () => {
                    setHeartbeats((prev) => ({ ...prev, [id]: null }));
                    markSeen(id);
                }
            )
        );
        return () => unsubs.forEach((u) => u());
    }, []);

    useEffect(() => {
        const t = setInterval(() => setNowMs(Date.now()), FRESHNESS_TICK_MS);
        return () => clearInterval(t);
    }, []);

    const build = (id, label) => {
        const data = heartbeats[id];
        const ageMs = heartbeatAgeMs(data?.updatedAt, nowMs);
        const running = ageMs != null && ageMs < SERVICE_STALE_MS;
        return {
            id,
            label,
            running,
            lastSeen: formatLastSeen(ageMs),
            loading,
        };
    };

    return {
        loading,
        services: [
            build('scheduler', 'Scheduler'),
            build('ai-help', 'Jules AI Help'),
        ],
    };
}

export { useCollection, useAllSystems, useServiceStatus }

